import {
  get,
  findIndex,
  cloneDeep,
  map,
  filter,
  indexOf,
} from 'es-toolkit/compat'
import * as c from 'common/c'
import type { GeoJsonObject } from 'geojson'
import { type LayerType } from 'common/layer-data'
import { swathParameters } from 'components/swath-selector'

declare module 'state/field.js' {
  export type FieldIntegrationData = {
    id: string
    activeBoundary: string
    addBoundary: string
    addGuidanceTrack: string
    baseStationAssociation: string
    boundaries: string
    clients: string
    createMapLayerSummary: string
    deleteField: string
    editField: string
    farms: string
    fieldOperation: string
    guidanceLines: string
    lastFieldOperation: string
    mapLayerSummaries: string
    owningOrganization: string
    self: string
    simplifiedBoundaries: string
    varietyLocator: string
    varietySummary: string
  }
  export type Revision = {
    createdAt: string
    field_id: string
    id: string
    revision: string
    tenantId: string
  }
  export type FieldModel = {
    id: number
    name: string
    farmId: number
    comments?: string
    createdAt?: string
    deletedAt?: string | null
    tenantId: string
    updatedAt: string
    integrationIds: { [key: string]: FieldIntegrationData }
    revisions: Revision[]
    areaSqm: number
    retiredAt?: string | null
    boundarySvg?: string
  }
  export type LayerInfoModel = {
    bboxMaxx: number
    bboxMaxy: number
    bboxMinx: number
    bboxMiny: number
    createdAt: string
    harvestId: number
    id: number
    layerName: string
    layerTitle: string
    layerType: string
    legacyCustomerId: null
    legacySeason: null | number | string
    legendUrl: string
    metadata: string
    queryable: boolean
    tenantId: string
    updatedAt: string
    year: number
    customLegends?: CustomLegend[]
    customLegendMetadataParam?: string
  }

  export type CustomLegend = {
    id: number
    legendData: LegendData
    tenantId: string
    parameter: string
    harvestId: number | null
    cropId: number | null
    // Season a crop legend applies to. Null means every year, and is outranked
    // by a legend scoped to the harvest's own crop year.
    cropYear: number | null
    createdAt: string
    updatedAt: string
  }

  export type LegendData = {
    num_ranges: number
    palette: string
    styleType?: 'gradient' | 'hillshade_color' | 'hillshade'
    ranges: {
      c: Array<string>
      q: Array<number>
    }
  }

  export type FieldFeature = {
    id: number
    featureId?: number
    properties: { [key: string]: string }
    geometry: GeoJsonObject & {
      crs: { properties: { name: string }; type: string }
    }
    cropId: number
  }

  export type FieldImportItem = {
    geom_type: string
    geo_object: string
  } & { [key: string]: string }

  export type LegendTypeItem = {
    layerName: string
    useCustomLegend: boolean
    harvestId: number
    swathParameter?: string
  }

  export interface Field {
    collection: FieldModel[]
    singleton: FieldModel | undefined
    fieldsHarvested: FieldModel[]
    fieldsHarvested_a: FieldModel[]
    fieldsHarvested_b: FieldModel[]
    featuresOverview: FieldFeature[]
    crop: string | number
    editFieldName: boolean
    year: number | undefined
    year_a: number | undefined
    year_b: number | undefined
    field_a: FieldModel | undefined
    field_b: FieldModel | undefined
    layerInfo: LayerInfoModel[] | undefined
    layerInfo_a: undefined | LayerInfoModel[]
    layerInfo_b: undefined | LayerInfoModel[]
    features: symbol | FieldFeature
    features_a: symbol | FieldFeature
    features_b: symbol | FieldFeature
    defaultExtent: number[] | undefined
    // Extent of every client's fields, regardless of the client selection
    tenantExtent: number[] | undefined
    currentFeatures: Map<string, FieldFeature>
    layers: { [key: string | number]: { [key: string]: LayerType } }
    layers_a: { [key: string | number]: { [key: string]: LayerType } }
    layers_b: { [key: string | number]: { [key: string]: LayerType } }
    fieldsImported:
      undefined | { properties: string[]; fields: FieldImportItem[] }
    cropLegends: CustomLegend[]
    legendTypes: LegendTypeItem[]
  }
}

const initialState: Field = {
  collection: [],
  fieldsHarvested: [],
  singleton: undefined,
  features: c.UNDEFINED,
  features_a: c.UNDEFINED,
  features_b: c.UNDEFINED,
  featuresOverview: [],
  defaultExtent: undefined,
  tenantExtent: undefined,
  currentFeatures: new Map(),
  layers: {},
  layers_a: {},
  layers_b: {},
  layerInfo: undefined,
  layerInfo_a: undefined,
  layerInfo_b: undefined,
  fieldsImported: undefined,
  fieldsHarvested_a: [],
  fieldsHarvested_b: [],
  field_a: undefined,
  field_b: undefined,
  crop: 'all',
  year: undefined,
  year_a: undefined,
  year_b: undefined,
  editFieldName: false,
  cropLegends: [],
  legendTypes: [],
}

function compareFieldName(a, b) {
  //@ts-expect-error
  const lang = navigator.language || navigator.userLanguage
  //This should sort everything correctly (or mostly correctly) based on
  //users language might have still have issues if names are in two different
  //languages (then again I'm not sure if there are any good ways to handle that)
  const aName = a?.name?.toLocaleLowerCase(lang)
  const bName = b?.name?.toLocaleLowerCase(lang)
  // Sort things in numeric order, ie field 1, field 2, field 11
  // instead of field 1, field 11, 2.
  return aName.localeCompare(bName, lang, { numeric: true })
}

export default (state = initialState, action) => {
  switch (action.type) {
    case c.FIELDS_FETCHED: {
      action.payload.sort((a, b) => {
        return compareFieldName(a, b)
      })
      //@ts-expect-error
      return update(state, { collection: { $set: action.payload } })
    }

    case c.HARVESTED_FIELDS_FETCHED: {
      action.payload.sort((a, b) => {
        return compareFieldName(a, b)
      })
      //@ts-expect-error
      return update(state, { fieldsHarvested: { $set: action.payload } })
    }

    case c.SET_DEFAULT_EXTENT: {
      //@ts-expect-error
      return update(state, { defaultExtent: { $set: action.payload } })
    }

    case c.SET_TENANT_EXTENT: {
      //@ts-expect-error
      return update(state, { tenantExtent: { $set: action.payload } })
    }

    case c.SET_FEATURES_OVERVIEW: {
      const featuresOverview: FieldFeature[] = []
      for (let i = 0; i < action.payload.length; i++) {
        const { id, fieldId, geometry, properties, cropId } = action.payload[i]
        featuresOverview.push({
          id: fieldId,
          featureId: id,
          geometry: geometry,
          properties,
          cropId,
        })
      } //@ts-expect-error
      return update(state, { featuresOverview: { $set: featuresOverview } })
    }

    case c.UPDATE_FEATURES_OVERVIEW: {
      const featuresOverview = cloneDeep(state.featuresOverview)
      const fieldList = cloneDeep(state.collection)

      for (let i = 0; i < action.payload.length; i++) {
        const { fieldId, geometry, properties, boundarySvg, areaSqm, cropId } =
          action.payload[i]
        const fieldIndex = fieldList.findIndex((p) => p.id === fieldId)
        const index = featuresOverview.findIndex((p) => p.id === fieldId)
        if (index > -1) {
          featuresOverview[index] = {
            id: fieldId,
            featureId: featuresOverview[index].featureId,
            geometry,
            properties,
            cropId,
          }
        } else {
          // If featureId is missing in payload, it remains undefined.
          // This is acceptable as new features might get fully set via SET_FEATURES_OVERVIEW later.
          const { id } = action.payload[i]
          featuresOverview.push({
            id: fieldId,
            featureId: id, // Assuming payload has it, if not undefined
            geometry,
            properties,
            cropId,
          })
        }

        if (fieldIndex > -1 && boundarySvg) {
          const field = fieldList[fieldIndex]
          fieldList[fieldIndex] = {
            ...field,
            ...{ boundarySvg: boundarySvg, areaSqm: areaSqm },
          }
        }
      }
      //@ts-expect-error
      return update(state, {
        featuresOverview: { $set: featuresOverview },
        collection: { $set: fieldList },
      })
    }

    case c.UPDATE_COUNTRY_EXTENT: {
      //@ts-expect-error
      return update(state, { defaultExtent: { $set: action.payload } })
    }

    case c.EDIT_FIELD: {
      const { id } = action.payload

      const index = state.collection.findIndex((p) => p.id === id)

      //@ts-expect-error
      const oToUpdate: Partial<Field> = { singleton: { $set: action.payload } }

      if (index > -1) {
        //@ts-expect-error
        oToUpdate.collection = { [index]: { $set: action.payload } }
      }

      //@ts-expect-error
      return update(state, oToUpdate)
    }

    case c.DELETE_FIELD: {
      const oToUpdate = {
        features: { $set: [] },
        currentFeatures: { $set: new Map() },
        singleton: { $set: undefined },
      }

      const idx = findIndex(
        get(state, 'collection', []),
        (field) => field.id === action.payload,
      )
      if (idx > -1) {
        //@ts-expect-error
        oToUpdate.collection = { $splice: [[idx, 1]] }
      }

      const featureIdx = findIndex(
        get(state, 'featuresOverview', []),
        (field) => field.id === action.payload,
      )
      if (featureIdx > -1) {
        //@ts-expect-error
        oToUpdate.featuresOverview = { $splice: [[featureIdx, 1]] }
      }

      //@ts-expect-error
      return update(state, oToUpdate)
    }

    case c.FIELD_FETCHED: {
      //@ts-expect-error
      return update(state, { singleton: { $set: action.payload } })
    }

    case c.FIELD_FETCHED_A: {
      //@ts-expect-error
      return update(state, { field_a: { $set: action.payload } })
    }

    case c.FIELD_FETCHED_B: {
      //@ts-expect-error
      return update(state, { field_b: { $set: action.payload } })
    }

    case c.ADD_FIELD_FEATURE: {
      const feature = action.payload
      //@ts-expect-error
      return update(state, {
        currentFeatures: { $add: [[feature.id, feature]] },
      })
    }

    case c.SET_FIELD_FEATURES: {
      //@ts-expect-error
      return update(state, { currentFeatures: { $set: action.payload } })
    }

    case c.CLEAR_FIELD_FEATURES: {
      //@ts-expect-error
      return update(state, {
        features: { $set: [] },
        currentFeatures: { $set: new Map() },
      })
    }

    case c.FEATURES_FETCHED: {
      //@ts-expect-error
      return update(state, { features: { $set: action.payload } })
    }

    case c.FEATURES_A_FETCHED: {
      //@ts-expect-error
      return update(state, { features_a: { $set: action.payload } })
    }

    case c.FEATURES_B_FETCHED: {
      //@ts-expect-error
      return update(state, { features_b: { $set: action.payload } })
    }

    case c.SET_LAYERS: {
      //@ts-expect-error
      return update(state, { layers: { $merge: action.payload } })
    }

    case c.SET_CROP: {
      //@ts-expect-error
      return update(state, { crop: { $set: action.payload.data } })
    }

    case c.SET_YEAR: {
      //@ts-expect-error
      return update(state, { year: { $set: action.payload.data } })
    }

    case c.SET_YEAR_A: {
      //@ts-expect-error
      return update(state, { year_a: { $set: action.payload.data } })
    }

    case c.SET_YEAR_B: {
      //@ts-expect-error
      return update(state, { year_b: { $set: action.payload.data } })
    }

    case c.SET_LAYERS_A: {
      //@ts-expect-error
      return update(state, { layers_a: { $merge: action.payload } })
    }

    case c.SET_LAYERS_B: {
      //@ts-expect-error
      return update(state, { layers_b: { $merge: action.payload } })
    }

    case c.RESET_LAYERS: {
      //@ts-expect-error
      return update(state, {
        layers: {
          $set: {},
        },
      })
    }

    case c.RESET_LAYERS_A: {
      //@ts-expect-error
      return update(state, {
        layers_a: {
          $set: {},
        },
      })
    }

    case c.RESET_LAYERS_B: {
      //@ts-expect-error
      return update(state, {
        layers_b: {
          $set: {},
        },
      })
    }

    case c.LAYER_INFO_FETCHED: {
      //@ts-expect-error
      return update(state, { layerInfo: { $set: action.payload } })
    }

    case c.LAYER_INFO_A_FETCHED: {
      //@ts-expect-error
      return update(state, { layerInfo_a: { $set: action.payload } })
    }

    case c.LAYER_INFO_B_FETCHED: {
      //@ts-expect-error
      return update(state, { layerInfo_b: { $set: action.payload } })
    }

    case c.FIELDS_HARVESTED_A_FETCHED: {
      action.payload.sort((a, b) => {
        return compareFieldName(a, b)
      })
      //@ts-expect-error
      return update(state, { fieldsHarvested_a: { $set: action.payload } })
    }

    case c.FIELDS_HARVESTED_B_FETCHED: {
      action.payload.sort((a, b) => {
        return compareFieldName(a, b)
      })
      //@ts-expect-error
      return update(state, { fieldsHarvested_b: { $set: action.payload } })
    }

    case c.FIELDS_IMPORTED: {
      //@ts-expect-error
      return update(state, { fieldsImported: { $set: action.payload } })
    }

    case c.DELETE_FIELDS: {
      const ids = action.payload
      let final: FieldModel[] = []
      let finalOverview: FieldFeature[] = []

      const oToUpdate: Partial<Field> = {}

      for (var i = 0; i < ids.length; i++) {
        final =
          final.length > 0
            ? final.filter((p) => p.id != ids[i])
            : state.collection.filter((p) => p.id != ids[i])
        finalOverview =
          finalOverview.length > 0
            ? finalOverview.filter((p) => p.id != ids[i])
            : state.featuresOverview.filter((p) => p.id != ids[i])
      }
      oToUpdate.collection = final
      oToUpdate.featuresOverview = finalOverview
      console.log(oToUpdate) // to avoid linter error about unused variable
      //@ts-expect-error
      return update(state, {
        collection: { $set: final },
        featuresOverview: { $set: finalOverview },
      })
    }

    case c.SELECT_FIELD_A: {
      //@ts-expect-error
      return update(state, { field_a: { $set: action.payload } })
    }

    case c.SELECT_FIELD_B: {
      //@ts-expect-error
      return update(state, { field_b: { $set: action.payload } })
    }

    case c.EDIT_FIELD_NAME: {
      //@ts-expect-error
      return update(state, { editFieldName: { $set: action.payload } })
    }

    case c.LEGEND_CREATED: {
      const response = action.payload
      const layerInfo = cloneDeep(state.layerInfo || [])
      let updatedLayerInfo: LayerInfoModel[] = []
      if (response.cropId) {
        const cropLegends = cloneDeep(state.cropLegends || [])
        const existingIndex = cropLegends.findIndex(
          (cl) => cl.id === response.id,
        )
        if (existingIndex > -1) {
          cropLegends[existingIndex] = response
        } else {
          cropLegends.push(response)
        }

        /*
         * Only layers belonging to harvests this legend actually covers. Without
         * the harvest filter every loaded layer of a custom-legend type gets the
         * legend, so saving a 2025 canola legend immediately recolours 2024
         * harvests — and harvests of other crops — until the next refetch.
         *
         * harvestIds is supplied by the legend thunks. When it is absent the
         * caller had no harvest context, so the old unscoped behaviour stands.
         */
        const scopedHarvestIds = action.meta?.harvestIds
        const indices = map(
          filter(
            layerInfo,
            (item) =>
              c.customLegendLayerTypes.includes(item.layerName) &&
              (scopedHarvestIds === undefined ||
                scopedHarvestIds.includes(item.harvestId)),
          ),
          (item) => indexOf(layerInfo, item),
        )
        for (let i = 0; i < indices.length; i++) {
          const li = layerInfo[indices[i]]
          const legendIndex = findIndex(
            li.customLegends || [],
            (cl) => cl.parameter === response.parameter,
          )
          if (li.customLegends) {
            if (legendIndex > -1) {
              li.customLegends[legendIndex] = response
            } else if (
              li.customLegends.length === 0 ||
              response.parameter === li?.customLegendMetadataParam ||
              li.layerName === 'swath'
            ) {
              //This check might need to change if there is another layer that uses a parameter that swath maps don't have
              li.customLegends.push(response)
            }
          }
          layerInfo[indices[i]] = li
        }
        //@ts-expect-error
        return update(state, {
          layerInfo: { $set: layerInfo },
          cropLegends: { $set: cropLegends },
        })
      } else if (response.harvestId) {
        const swathSelected = swathParameters.includes(response.parameter)
        const index = layerInfo.findIndex(
          (l) =>
            l.harvestId === response.harvestId &&
            l.layerTitle === (swathSelected ? 'swath' : response.parameter),
        )
        if (index > -1) {
          updatedLayerInfo = [...layerInfo]
          if (swathSelected) {
            if (
              updatedLayerInfo[index] &&
              Array.isArray(updatedLayerInfo[index].customLegends)
            ) {
              const clIndex = updatedLayerInfo[index].customLegends.findIndex(
                (cl) => cl.parameter === response.parameter,
              )
              if (clIndex > -1) {
                //@ts-expect-error
                updatedLayerInfo = update(updatedLayerInfo, {
                  [index]: {
                    customLegends: {
                      [clIndex]: { $set: response },
                    },
                  },
                })
              } else {
                updatedLayerInfo[index].customLegends.push(response)
              }
            }
          } else {
            updatedLayerInfo[index] = {
              ...updatedLayerInfo[index],
              customLegends: [response],
            }
          }
        }
        if (response.parameter === 'yield') {
          const yieldIndex = updatedLayerInfo.findIndex(
            (l) =>
              l.harvestId === response.harvestId &&
              l.layerTitle === 'corrected_yield',
          )
          if (yieldIndex > -1) {
            updatedLayerInfo[yieldIndex] = {
              ...updatedLayerInfo[yieldIndex],
              customLegends: [response],
            }
          }
        }
        //@ts-expect-error
        return update(state, { layerInfo: { $set: updatedLayerInfo } })
      } else if (response.parameter === 'dem') {
        updatedLayerInfo = layerInfo.map((layer) => {
          if (layer.layerName !== 'dem') {
            return layer
          }

          const existingLegends = Array.isArray(layer.customLegends)
            ? [...layer.customLegends]
            : []
          const harvestSpecificIndex = existingLegends.findIndex(
            (legend) =>
              legend?.parameter === 'dem' &&
              legend?.harvestId === layer.harvestId,
          )
          const globalIndex = existingLegends.findIndex(
            (legend) =>
              legend?.parameter === 'dem' &&
              legend?.harvestId == null &&
              legend?.cropId == null,
          )

          if (response.harvestId != null) {
            if (layer.harvestId === response.harvestId) {
              if (harvestSpecificIndex > -1) {
                existingLegends[harvestSpecificIndex] = response
              } else {
                existingLegends.unshift(response)
              }
              return {
                ...layer,
                customLegends: existingLegends,
              }
            }

            return layer
          }

          if (harvestSpecificIndex > -1) {
            if (globalIndex > -1) {
              existingLegends[globalIndex] = response
            } else {
              existingLegends.push(response)
            }

            return {
              ...layer,
              customLegends: existingLegends,
            }
          }

          return {
            ...layer,
            customLegends: [response],
          }
        })

        //@ts-expect-error
        return update(state, { layerInfo: { $set: updatedLayerInfo } })
      }
    }

    case c.CUSTOM_LEGENDS_FETCHED: {
      //@ts-expect-error
      return update(state, { cropLegends: { $set: action.payload } })
    }

    case c.CUSTOM_LEGEND_DELETED: {
      const id = action.payload
      const type = action?.meta?.type
      const layer = action?.meta?.layer
      const swathParameter = action?.meta?.swathParameter
      if (!type) {
        const cropLegends = cloneDeep(state.cropLegends || [])
        const index = cropLegends.findIndex((cl) => cl.id === id)
        if (index > -1) {
          cropLegends.splice(index, 1)
          //@ts-expect-error
          return update(state, { cropLegends: { $set: cropLegends } })
        }
      } else if (type === 'layer') {
        let layerInfo = cloneDeep(state.layerInfo || [])

        const deleteLegendFromLayer = (layerName: string, legendId) => {
          return layerInfo.map((layerEntry) => {
            if (layerEntry.layerName === layerName) {
              if (Array.isArray(layerEntry.customLegends)) {
                const legendIndex = layerEntry.customLegends.findIndex(
                  (l) => l?.id === legendId,
                )
                if (legendIndex > -1) {
                  const updatedLegends = [...layerEntry.customLegends]
                  updatedLegends.splice(legendIndex, 1)
                  return {
                    ...layerEntry,
                    customLegends: updatedLegends,
                  }
                }
              }
            }
            return layerEntry
          })
        }

        if (layer.name === 'corrected_yield') {
          //update the swath yield too
          layerInfo = deleteLegendFromLayer('swath', id)
        } else if (layer.name === 'swath' && swathParameter === 'yield') {
          //update the corrected yield too
          layerInfo = deleteLegendFromLayer('corrected_yield', id)
        }

        layerInfo = deleteLegendFromLayer(layer.name, id)
        //@ts-expect-error
        return update(state, { layerInfo: { $set: layerInfo } })
      }
    }

    case c.SET_LEGEND_TYPE: {
      const obj = action.payload
      const legendTypeCollection: LegendTypeItem[] = cloneDeep(
        state.legendTypes || [],
      )

      let index
      if (obj.layerName === 'swath') {
        index = legendTypeCollection.findIndex(
          (l) =>
            l.layerName === obj.layerName &&
            l.harvestId === obj.harvestId &&
            l.swathParameter === obj.swathParameter,
        )
      } else {
        index = legendTypeCollection.findIndex(
          (l) => l.harvestId === obj.harvestId && l.layerName === obj.layerName,
        )
      }
      if (index > -1) {
        //@ts-ignore
        return update(state, {
          ['legendTypes']: { [index]: { $set: action.payload } },
        })
      } else {
        //@ts-ignore
        return update(state, { legendTypes: { $push: [action.payload] } })
      }
    }

    default:
      return state
  }
}
